import Entity from "../../model/Entity"
import CollectionUtil from "../../util/CollectionUtil"
import ObjectUtil from "../../util/ObjectUtil"
import { SERVICE_TYPES, ServiceType } from "../../model/ServiceType"
import {
  DecisionReason,
  Delay,
  ExperimentType,
  InAppMessageContext,
  InAppMessageContextMessage,
  InAppMessageEvaluateContext,
  InAppMessageEventFrequencyCap,
  InAppMessageEventTrigger,
  InAppMessageEventTriggerRule,
  InAppMessageKey,
  InAppMessagePeriod,
  InAppMessagePeriodAlways,
  InAppMessageTimetable,
  InAppMessageTimetableAll,
  MATCH_VALUE_TYPES,
  parseExperimentStatusOrNull,
  parseInAppMessagePeriodOrNull,
  ParameterConfiguration,
  Properties,
  RemoteConfigParameterValue,
  SplitUrlTargets,
  Variation
} from "../../model/model"
import ExperimentRemoteEvaluateResult from "./entity/ExperimentRemoteEvaluateResult"
import InAppMessageEligibilityRemoteEvaluateResult from "./entity/InAppMessageEligibilityRemoteEvaluateResult"
import InAppMessageLayoutRemoteEvaluateResult from "./entity/InAppMessageLayoutRemoteEvaluateResult"
import RemoteConfigParameterRemoteEvaluateResult from "./entity/RemoteConfigParameterRemoteEvaluateResult"
import RemoteEvaluateResult from "./entity/RemoteEvaluateResult"
import {
  EntityDto,
  EntityEvaluationDto,
  ExperimentEvaluateResultDto,
  InAppMessageEligibilityEvaluateResultDto,
  InAppMessageLayoutEvaluateResultDto,
  RemoteConfigParameterEvaluateResultDto,
  WorkspaceEvaluationDto
} from "./model/dto"
import Workspace from "../Workspace"
import { WorkspaceConfigMetadata } from "../config/WorkspaceConfig"
import { Attributes } from "../dto"
import { PropertiesBuilder } from "../../../../hackle/property/PropertiesBuilder"

/**
 * Workspace evaluation metadata. `id`/`environmentId` identify the workspace;
 * `modifiedAt` comes from the server evaluation metadata.
 */
export interface WorkspaceEvaluationMetadata extends WorkspaceConfigMetadata {
  evaluatedAt: number
}

/**
 * A per-user workspace evaluation resolved by the server, built from a server response.
 * Read APIs query it synchronously. Experiment/featureFlag/remoteConfig/inAppMessage results are parsed.
 */
export default class WorkspaceEvaluation implements Workspace {
  private readonly experimentsByKey: Map<number, ExperimentRemoteEvaluateResult>
  private readonly featureFlagsByKey: Map<number, ExperimentRemoteEvaluateResult>
  private readonly rcByKey: Map<string, RemoteConfigParameterRemoteEvaluateResult>
  private readonly inAppMessagesByKey: Map<number, InAppMessageEligibilityRemoteEvaluateResult>

  constructor(
    readonly metadata: WorkspaceEvaluationMetadata,
    private readonly experiments: ExperimentRemoteEvaluateResult[],
    private readonly featureFlags: ExperimentRemoteEvaluateResult[],
    private readonly remoteConfigParameters: RemoteConfigParameterRemoteEvaluateResult[],
    private readonly inAppMessages: InAppMessageEligibilityRemoteEvaluateResult[],
    readonly fullEvaluatedAt: number | null = null,
    private readonly attributes: Attributes = {}
  ) {
    this.experimentsByKey = CollectionUtil.associateBy(experiments, (it) => it.key)
    this.featureFlagsByKey = CollectionUtil.associateBy(featureFlags, (it) => it.key)
    this.rcByKey = CollectionUtil.associateBy(remoteConfigParameters, (it) => it.key)
    this.inAppMessagesByKey = CollectionUtil.associateBy(inAppMessages, (it) => it.key)
  }

  getExperimentOrNull(experimentKey: number): ExperimentRemoteEvaluateResult | undefined {
    return this.experimentsByKey.get(experimentKey)
  }

  getFeatureFlagOrNull(featureKey: number): ExperimentRemoteEvaluateResult | undefined {
    return this.featureFlagsByKey.get(featureKey)
  }

  getExperiments(): ExperimentRemoteEvaluateResult[] {
    return this.experiments
  }

  getFeatureFlags(): ExperimentRemoteEvaluateResult[] {
    return this.featureFlags
  }

  getRemoteConfigParameters(): RemoteConfigParameterRemoteEvaluateResult[] {
    return this.remoteConfigParameters
  }

  getInAppMessages(): InAppMessageEligibilityRemoteEvaluateResult[] {
    return this.inAppMessages
  }

  getRemoteConfigParameterOrNull(key: string): RemoteConfigParameterRemoteEvaluateResult | undefined {
    return this.rcByKey.get(key)
  }

  getInAppMessageOrNull(key: InAppMessageKey): InAppMessageEligibilityRemoteEvaluateResult | undefined {
    return this.inAppMessagesByKey.get(key)
  }

  getAttributes(): Attributes {
    return this.attributes
  }

  toProperties(): Properties {
    const properties = new PropertiesBuilder()
      .add("remote_evaluated_at", this.metadata.evaluatedAt)
      .add("config_modified_at", this.metadata.modifiedAt)
      .add("remote_full_evaluated_at", this.fullEvaluatedAt)
      .add("evaluation_mode", "REMOTE")
      .build()

    return ObjectUtil.fromMap(properties)
  }

  result(entity: Entity): RemoteEvaluateResult | undefined {
    switch (entity.serviceType) {
      case "AB_TEST":
        return this.experiments.find((it) => it.id === entity.id)
      case "FEATURE_FLAG":
        return this.featureFlags.find((it) => it.id === entity.id)
      case "REMOTE_CONFIG":
        return this.remoteConfigParameters.find((it) => it.id === entity.id)
      case "IN_APP_MESSAGE":
        return this.inAppMessages.find((it) => it.id === entity.id)
      default:
        return undefined
    }
  }

  // Accepts a whole-workspace evaluation or an entity (partial) evaluation; only the shared fields
  // (workspace, results, metadata.evaluatedAt/config) are read.
  static from(
    dto: WorkspaceEvaluationDto | EntityEvaluationDto,
    fullEvaluatedAt: number | null = null
  ): WorkspaceEvaluation {
    const experiments: ExperimentRemoteEvaluateResult[] = []
    const featureFlags: ExperimentRemoteEvaluateResult[] = []
    const remoteConfigParameters: RemoteConfigParameterRemoteEvaluateResult[] = []
    const inAppMessages: InAppMessageEligibilityRemoteEvaluateResult[] = []

    for (const result of dto.results) {
      switch (parseServiceTypeOrNull(result.type)) {
        case "AB_TEST":
          if (result.experiment) {
            const experiment = toExperimentResult(result.experiment, "AB_TEST")
            if (experiment) experiments.push(experiment)
          }
          break
        case "FEATURE_FLAG":
          if (result.featureFlag) {
            const featureFlag = toExperimentResult(result.featureFlag, "FEATURE_FLAG")
            if (featureFlag) featureFlags.push(featureFlag)
          }
          break
        case "REMOTE_CONFIG":
          if (result.remoteConfig) {
            const rc = toRemoteConfigResult(result.remoteConfig)
            if (rc) remoteConfigParameters.push(rc)
          }
          break
        case "IN_APP_MESSAGE":
          if (result.inAppMessage) {
            const iam = toInAppMessageResult(result.inAppMessage)
            if (iam) inAppMessages.push(iam)
          }
          break
        default:
          break
      }
    }

    // Bulk enumeration and in-app message first-eligible-wins both depend on the server-assigned
    // order, and delta merge does not preserve wire order, so ordering is restored here.
    experiments.sort((a, b) => a.order - b.order)
    featureFlags.sort((a, b) => a.order - b.order)
    inAppMessages.sort((a, b) => a.order - b.order)

    const metadata: WorkspaceEvaluationMetadata = {
      id: dto.workspace.id,
      environmentId: dto.workspace.environment.id,
      modifiedAt: dto.metadata.config.modifiedAt,
      evaluatedAt: dto.metadata.evaluatedAt
    }

    return new WorkspaceEvaluation(
      metadata,
      experiments,
      featureFlags,
      remoteConfigParameters,
      inAppMessages,
      fullEvaluatedAt,
      dto.workspace.attributes
    )
  }
}

function toExperimentResult(
  dto: ExperimentEvaluateResultDto,
  type: ExperimentType
): ExperimentRemoteEvaluateResult | undefined {
  const status = parseExperimentStatusOrNull(dto.execution.status)
  if (status === undefined) return undefined

  const config = dto.config ? ParameterConfiguration.fromJson(dto.config) : undefined
  const variation = new Variation(dto.variation.id, dto.variation.key, dto.variation.status === "DROPPED", config)
  return new ExperimentRemoteEvaluateResult(
    dto.id,
    dto.key,
    dto.version,
    status,
    dto.order,
    type,
    dto.execution.version,
    variation,
    DecisionReason.from(dto.reason),
    toEntities(dto.references),
    SplitUrlTargets.fromJson(dto.execution.targetUrls ?? null)
  )
}

function toRemoteConfigResult(
  dto: RemoteConfigParameterEvaluateResultDto
): RemoteConfigParameterRemoteEvaluateResult | undefined {
  const type = MATCH_VALUE_TYPES.find((it) => it === dto.valueType)
  if (type === undefined) return undefined
  const value = dto.value ? RemoteConfigParameterValue.fromJson(dto.value) : undefined
  return new RemoteConfigParameterRemoteEvaluateResult(
    dto.id,
    dto.key,
    type,
    value,
    DecisionReason.from(dto.reason),
    toEntities(dto.references)
  )
}

function toInAppMessageResult(
  dto: InAppMessageEligibilityEvaluateResultDto
): InAppMessageEligibilityRemoteEvaluateResult | undefined {
  // A missing period means always-on; a present but unparseable period drops the result.
  const period = ObjectUtil.isNullOrUndefined(dto.period)
    ? new InAppMessagePeriodAlways()
    : parseInAppMessagePeriodOrNull(
        dto.period.type,
        dto.period.startMillisInclusive ?? null,
        dto.period.endMillisExclusive ?? null
      )
  if (period === undefined) return undefined

  const timetable = (dto.timetable && InAppMessageTimetable.fromJson(dto.timetable)) ?? new InAppMessageTimetableAll()

  const rules = dto.eventTriggerRules.map(InAppMessageEventTriggerRule.fromJson)
  const frequencyCap = InAppMessageEventFrequencyCap.fromJson(dto.eventFrequencyCap)
  const delay = (dto.eventTriggerDelay && Delay.fromJson(dto.eventTriggerDelay)) ?? Delay.immediate()
  const eventTrigger = new InAppMessageEventTrigger(rules, frequencyCap, delay)

  const messageContext = InAppMessageContext.fromJson(dto.messageContext)
  if (messageContext === undefined) return undefined

  const evaluateContext = InAppMessageEvaluateContext.fromJson(dto.evaluateContext ?? { atDeliverTime: false })

  const layout = toLayoutResult(dto, dto.layout, period, timetable, eventTrigger, evaluateContext, messageContext)
  if (layout === undefined) return undefined

  return new InAppMessageEligibilityRemoteEvaluateResult(
    dto.id,
    dto.key,
    dto.order,
    period,
    timetable,
    eventTrigger,
    evaluateContext,
    messageContext,
    dto.isEligible,
    DecisionReason.from(dto.reason),
    toEntities(dto.references),
    layout
  )
}

function toLayoutResult(
  eligibilityDto: InAppMessageEligibilityEvaluateResultDto,
  layoutDto: InAppMessageLayoutEvaluateResultDto,
  period: InAppMessagePeriod,
  timetable: InAppMessageTimetable,
  eventTrigger: InAppMessageEventTrigger,
  evaluateContext: InAppMessageEvaluateContext,
  messageContext: InAppMessageContext
): InAppMessageLayoutRemoteEvaluateResult | undefined {
  const message = InAppMessageContextMessage.fromJson(layoutDto.message)
  if (message === undefined) return undefined

  return new InAppMessageLayoutRemoteEvaluateResult(
    eligibilityDto.id,
    eligibilityDto.key,
    eligibilityDto.order,
    period,
    timetable,
    eventTrigger,
    evaluateContext,
    messageContext,
    message,
    DecisionReason.from(layoutDto.reason),
    toEntities(layoutDto.references)
  )
}

function toEntities(references: EntityDto[]): Entity[] {
  return CollectionUtil.mapNotNullOrUndefined(references, (reference) => {
    const serviceType = parseServiceTypeOrNull(reference.type)
    if (serviceType === undefined) return undefined
    return { id: reference.id, serviceType }
  })
}

function parseServiceTypeOrNull(type: string): ServiceType | undefined {
  return SERVICE_TYPES.find((it) => it === type)
}
